import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { ToastrService, IndividualConfig } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { FormGroup, FormArray } from '@angular/forms';
import { get, isArray, isPlainObject, forEach } from 'lodash';
import { SeoService } from './seo.service';

@Injectable({
  providedIn: 'root'
})
export class HelpersService {

  urlRegex: RegExp = new RegExp(/^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/);

  constructor(
    private toastr: ToastrService,
    private translate: TranslateService,
    private seo: SeoService
  ) { }

  /**
 * Display toastr error
 * @param error Custom Error Object
 * @param toastrOptions Overwrite default toastr options
 */
  errorMessage(error: any, toastrOptions?: IndividualConfig) {
    const message = get(error, 'error.error.userMessage') ||
      get(error, 'error.error.errorMessage') ||
      get(error, 'errorMessage') ||
      get(error, 'error.errors') ||
      get(error, 'userMessage') ||
      get(error, 'errorMessage') ||
      get(error, 'message') ||
      this.translate.instant('generic-error');

    if (isPlainObject(message)) {
      forEach(message, (val, key) => {
        if (isArray(val)) {
          val.forEach(msg => {
            this.toastr.error(msg, 'Error', toastrOptions);
          });
        } else {
          this.toastr.error(val, 'Error', toastrOptions);
        }
      });
      return;
    }

    if (isArray(message)) {
      message.forEach(item => {
        const msg = get(item, 'errorMessage');
        this.toastr.error(msg, 'Error', toastrOptions);
      });
      return;
    }

    this.toastr.info(message, 'Error', toastrOptions);
  }

  /**
  * Show warning toastr
  * @param message warning message
  * @param title  warning title
  * @param toastrOptions Overwrite default toastr options
  */
  warningMessage(message: string, title?: string, toastrOptions?: IndividualConfig) {
    return this.toastr.info(message, title ? title : '', toastrOptions);
  }

  /**
  * Show success toastr
  * @param message success message
  * @param title  success title
  * @param toastrOptions Overwrite default toastr options
  */
  successMessage(message: string, title?: string, toastrOptions?: IndividualConfig) {
    return this.toastr.success(message, title ? title : '', toastrOptions);
  }

  /**
  * Show info toastr
  * @param message success message
  * @param title  success title
  * @param toastrOptions Overwrite default toastr options
  */
  infoMessage(message: string, title?: string, toastrOptions?: IndividualConfig) {
    return this.toastr.info(message, title ? title : '', toastrOptions);
  }

  /**
   * Get a langs regex to validate browser lang
   */
  getLangRegex() {
    const langs = environment.langs ? environment.langs.join('|') : environment.defaults.lang;
    const regex = new RegExp(langs);
    return regex;
  }

  /**
   * Generate query string for HTTP get requests
   * @param payload options
   */
  queryOptions(payload: any) {
    let params = '';
    const keys = Object.keys(payload);
    // concat all parameters
    keys.forEach(key => {
      if (!params.length) {
        params = payload[key] ? `?${key}=${payload[key]}` : `&${key}`;
      } else {
        params += payload[key] ? `&${key}=${payload[key]}` : `&${key}`;
      }
    });

    return params;
  }

  setformAsValid(form: FormGroup) {
    const keys = Object.keys(form.controls);
    keys.forEach(key => {
      if (form.controls[key]) {
        form.controls[key].markAsTouched();
        form.controls[key].setErrors(null);
      }
    });
  }

  setFormError(form: FormGroup, valid?: Boolean) {
    const keys = Object.keys(form.controls);
    keys.forEach(key => {
      if (form.controls[key]) {
        form.controls[key].markAsTouched();
        form.controls[key].setErrors({ valid: valid || false, submitted: true });
      }
    });
  }

  displayFormErrors(form: FormGroup, messages: Array<any> = []) {
    const keys = Object.keys(form.controls);
    const childFormKey = keys.find(key => form.controls[key] instanceof FormGroup);

    keys.forEach(key => {
      const message = this.findMessage(messages, key);
      if (form.controls[key] && !form.controls[key].valid) {
        form.controls[key].markAsTouched();
        form.controls[key].setErrors({
          valid: false,
          submitted: true,
          message: message
        });
      }
    });

    if (childFormKey) {
      const childKeys = Object.keys(form.controls[childFormKey]['controls']);
      const childForm: any = form.controls[childFormKey];

      childKeys.forEach(key => {
        if (childForm.controls[key] && !childForm.controls[key].valid) {
          const message = this.findMessage(messages, key);
          childForm.controls[key].markAsTouched();
          childForm.controls[key].setErrors({
            valid: false,
            submitted: true,
            message: message
          });
        }
      });
    }
  }

  /**
  * Initialize translate service 
  * by getting the language from browser settings
  */
  bootstrapLang() {
    // specify app languages
    this.translate.addLangs(environment.langs || []);
    // get browser languages
    let browserLang = this.translate.getBrowserLang();
    browserLang = browserLang.match(this.getLangRegex()) ? browserLang : environment.defaults.lang;

    // check in session storage for user lang if not exists use browserlang
    const userLang = localStorage.getItem('eyrus-lang') || browserLang;

    // if do not exists on session storage, set browser lang
    if (!localStorage.getItem('eyrus-lang'))
      localStorage.setItem('eyrus-lang', browserLang);

    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setDefaultLang(environment.defaults.lang);

    // the lang to use, if the lang isn't available, it will use the current loader to get them
    this.translate.use(userLang);
  }

  updateLang(lang){
    localStorage.setItem('eyrus-lang', lang);
    this.bootstrapLang();
  }

  /**
   * Set meta keys provided for each section
   */
  setMetaKeys(config) {
    this.seo.generateTags(config);
  }

  private findMessage(messages: Array<any>, key: string) {
    let message = '';
    message = messages.find((e) => {
      return e.fieldName === key;
    });
    return get(message, 'errorMessage') || '';
  }

  /**
   * get lang from browser
   */
  getLang() {
    return localStorage.getItem('eyrus-lang') || 'en';
  }

  /**
   * Get images or videos files as base64 string
   * @param file 
   */
  getMediaAsBase64(file: File): Promise<any> {
    let reader: FileReader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onload = (e: any) => {
        resolve({ url: e.target.result, data: file });
      };

      reader.onerror = () => {
        return reject(this);
      };

      if (file.type.indexOf('image') !== -1 || file.type.indexOf('video') !== -1) {
        reader.readAsDataURL(file);
      }
      else
        reject(file);
    });
  }
  /**
     * Device with validation
     */
  deviceIsMobile() {
    return (window.innerWidth >= 700) ? false : true;
  }
}
